import { RadioChangeEvent } from "antd";
import { useAddToConferenceSessionMutation } from "api/mutations/forumAttendeeItinerary";
import { useAttendeesQuery } from "api/queries/attendees";
import { useForumTablesWithAvailableSeatsQuery } from "api/queries/forumFacilities";
import { useForumStreamsAndSeminarsAndSessionsQuery } from "api/queries/forumStreams";
import { useAssignedSpeakersQuery } from "api/queries/seminars";
import { Button, Col, Divider, Drawer, Form, Radio, Row, Select, Spin } from "components/styleguide";
import AttendeeStatus from "enums/AttendeeStatus";
import TableType from "enums/TableType";
import ErrorTypes from "error-handling/errorTypes";
import {
  AttendeeItineraryAddToConferenceSessionRequest,
  AttendeeModel,
  ForumTableAvailableSeatsModel,
  PageResponseAttendeeModel,
  SeminarsShortModel,
  SessionsShortModel,
  SpeakerForumAttendee,
  StreamsAndSeminarsAndSessionsShortResponseModel,
  StreamsShortModel,
} from "generated/api";
import { EventLayoutContext } from "layouts/EventLayout/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { toast } from "react-toastify";
import { getFormattedRangeDateTime } from "utils/dateUtils";

const { Option } = Select;

const ellipsisCell: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: "95%",
};

export type ManualSchedulingDrawerProps = {
  visible: boolean;
  onClose: () => void;
  onFinish?: (values: AttendeeItineraryAddToConferenceSessionRequest) => void;
  defaultAttendeeId?: number;
};

export const ManualSchedulingDrawer = ({
  visible,
  onClose,
  onFinish,
  defaultAttendeeId,
}: ManualSchedulingDrawerProps) => {
  const { t } = useTranslation();

  const { id: forumId, forum } = useOutletContext<EventLayoutContext>();

  const [streams, setStreams] = useState<Array<StreamsShortModel>>([]);
  const [seminars, setSeminars] = useState<Array<SeminarsShortModel>>([]);
  const [sessions, setSessions] = useState<Array<SessionsShortModel>>([]);

  const [attendees, setAttendees] = useState<Array<AttendeeModel>>([]);
  const [seminarSpeakers, setSeminarSpeakers] = useState<Array<SpeakerForumAttendee>>([]);

  const [attendingAsSpeaker, setAttendingAsSpeaker] = useState<boolean>(false);
  const [selectedStream, setSelectedStream] = useState<StreamsShortModel | null>(null);
  const [selectedSeminar, setSelectedSeminar] = useState<SeminarsShortModel | null>(null);
  const [selectedSession, setSelectedSession] = useState<SessionsShortModel | null>(null);

  const [form] = Form.useForm<AttendeeItineraryAddToConferenceSessionRequest>();

  const { data: streamsAndSeminarsAndSessions, isLoading: isStreamsAndSeminarsAndSessionsLoading } =
    useForumStreamsAndSeminarsAndSessionsQuery(
      { forumId },
      {
        enabled: visible,
        onSuccess: ({ data }: { data: StreamsAndSeminarsAndSessionsShortResponseModel }) => {
          setStreams(data.streams || []);

          if (selectedSession) {
            setSeminars(data.seminars?.filter((seminar) => seminar.streamId === selectedSession?.streamId) || []);

            setSessions(data.sessions?.filter((session) => session.seminarId === selectedSession?.seminarId) || []);
          } else if (selectedSeminar) {
            setSeminars(data.seminars?.filter((seminar) => seminar?.seminarId === selectedSeminar?.seminarId) || []);

            setSessions(data.sessions?.filter((session) => session?.seminarId === selectedSeminar?.seminarId) || []);
          } else if (selectedStream) {
            setSeminars(data.seminars?.filter((seminar) => seminar.streamId === selectedStream?.id) || []);

            setSessions(data.sessions?.filter((session) => session.streamId === selectedStream?.id) || []);
          } else {
            setSeminars(data.seminars || []);

            setSessions(data.sessions || []);
          }
        },
      },
    );

  const { data: attendeesData, isLoading: isAttendeeLoading } = useAttendeesQuery(
    { forumId, attendeeStatus: [AttendeeStatus.Attending, AttendeeStatus.AttendingReplacement] },
    {
      enabled: visible,
      onSuccess: ({ data }: { data: PageResponseAttendeeModel }) => {
        if (!attendingAsSpeaker) {
          setAttendees(data.items || []);
        } else if (attendingAsSpeaker && seminarSpeakers) {
          setAttendees(
            data.items?.filter((attendee) =>
              seminarSpeakers?.some((speaker: SpeakerForumAttendee) => speaker.id === attendee.attendId),
            ) || [],
          );
        }
      },
    },
  );

  const { isLoading: isSeminarSpeakersLoading } = useAssignedSpeakersQuery(
    { forumId, forumSeminarId: selectedSeminar?.seminarId },
    {
      enabled: visible && !!selectedSeminar,
      onSuccess: ({ data }: { data: Array<SpeakerForumAttendee> }) => {
        setSeminarSpeakers(data);

        if (attendingAsSpeaker) {
          setAttendees(
            attendeesData?.data?.items?.filter((attendee) =>
              data?.some((speaker: SpeakerForumAttendee) => speaker.id === attendee.attendId),
            ) || [],
          );
          form.setFieldValue("attendeeId", undefined);
        }
      },
    },
  );

  const {
    data: tablesResponse,
    isError: isTablesError,
    isLoading: isTablesLoading,
    isSuccess: isTablesSuccess,
  } = useForumTablesWithAvailableSeatsQuery(
    {
      forumId,
      forumBuildingId: selectedSession?.forumBuildingId || 0,
      forumFacilityId: selectedSession?.forumFacilityId || 0,
      timeSlotSessionId: selectedSession?.timeSlotSessionId,
    },
    {
      enabled: visible && !!selectedSession && !attendingAsSpeaker,
    },
  );

  const tables: Array<ForumTableAvailableSeatsModel> = tablesResponse?.data || [];
  const isTableRequired = !attendingAsSpeaker && isTablesSuccess && tables.length > 0;
  const isTableLookupBlockingSubmit = !attendingAsSpeaker && !!selectedSession && !isTablesSuccess;

  useEffect(() => {
    if (
      visible &&
      defaultAttendeeId &&
      attendees.length > 0 &&
      !form.getFieldValue("attendeeId") &&
      attendees.some((a) => a.attendId === defaultAttendeeId)
    ) {
      form.setFieldValue("attendeeId", defaultAttendeeId);
    }
  }, [visible, attendees, defaultAttendeeId, form]);

  const closeDrawer = () => {
    onClose();
  };

  const addToConferenceSessionMutation = useAddToConferenceSessionMutation();

  const handleSubmit = (values: AttendeeItineraryAddToConferenceSessionRequest) => {
    if (addToConferenceSessionMutation.isLoading) return;

    addToConferenceSessionMutation.mutate(
      {
        forumId,
        attendeeId: values.attendeeId as number,
        attendeeItineraryAddToConferenceSessionRequest: { ...values, forumId },
      },
      {
        onSuccess: () => {
          toast.success(t("Attendee successfully added to conference session."), { autoClose: 5000 });

          closeDrawer();

          onFinish?.(values);
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (error: any) => {
          const err = error.response;

          if (ErrorTypes.isOfType(err, ErrorTypes.InvalidEntityState)) {
            const meta = err.data?.meta;

            if (meta?.ExpectedObjectFields) {
              Object.entries(meta.ExpectedObjectFields).forEach(([k, v]) => {
                toast.error(`${v}`, { autoClose: 15000 });
              });
            }
          } else if (ErrorTypes.isOfType(err, ErrorTypes.ObjectNotFound)) {
            const meta = err.data?.meta;

            toast.error(`${meta.ObjectType} not found.`, { autoClose: 15000 });
          } else {
            toast.error(t("Failed to add attendee to conference session. Please try again."), { autoClose: 15000 });
          }
        },
      },
    );
  };

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setSelectedStream(null);
      setSelectedSeminar(null);
      setSelectedSession(null);
      setAttendingAsSpeaker(false);
    }
  }, [visible, form]);

  const handleStreamChange = (value: number) => {
    if (!value) {
      setSelectedStream(null);
      setSelectedSeminar(null);
      setSelectedSession(null);

      setSeminars(streamsAndSeminarsAndSessions?.data?.seminars || []);
      setSessions(streamsAndSeminarsAndSessions?.data?.sessions || []);

      return;
    }

    const newSelectedStream = streams.find((stream) => stream?.id === value);
    setSelectedStream(newSelectedStream || null);

    setSeminars(streamsAndSeminarsAndSessions?.data?.seminars?.filter((seminar) => seminar.streamId === value) || []);
    setSessions(streamsAndSeminarsAndSessions?.data?.sessions?.filter((session) => session.streamId === value) || []);

    if (selectedSeminar?.streamId !== value) {
      form.setFieldValue("forumSeminarId", undefined);
    }

    if (selectedSession?.streamId !== value) {
      form.setFieldValue("forumSessionId", undefined);
      form.setFieldValue("forumTableId", undefined);
    }

    form.validateFields(["forumStreamId", "forumSeminarId", "forumSessionId"]);
  };

  const handleSeminarChange = (value: number) => {
    if (!value) {
      setSelectedSeminar(null);

      if (!selectedStream) {
        setSessions(streamsAndSeminarsAndSessions?.data?.sessions || []);
      } else {
        setSessions(
          streamsAndSeminarsAndSessions?.data?.sessions?.filter((session) => session.streamId === selectedStream?.id) ||
            [],
        );
      }

      return;
    }

    const newSelectedSeminar = seminars.find((seminar) => seminar.seminarId === value);
    setSelectedSeminar(newSelectedSeminar || null);

    if (newSelectedSeminar) {
      const associatedStream = streams.find((stream) => stream.id === newSelectedSeminar.streamId);

      if (associatedStream && selectedStream?.id !== associatedStream.id) {
        form.setFieldValue("forumStreamId", associatedStream.id);
        setSelectedStream(associatedStream);
      }
    }

    setSeminars(
      streamsAndSeminarsAndSessions?.data?.seminars?.filter(
        (seminar) => seminar.streamId === newSelectedSeminar?.streamId,
      ) || [],
    );
    setSessions(streamsAndSeminarsAndSessions?.data?.sessions?.filter((session) => session.seminarId === value) || []);

    if (selectedSession?.seminarId !== value) {
      form.setFieldValue("forumSessionId", undefined);
      setSelectedSession(null);
    }

    form.setFieldValue("forumTableId", undefined);
    form.validateFields(["forumStreamId", "forumSeminarId", "forumSessionId"]);
  };

  const handleSessionChange = (value: number) => {
    if (!value) {
      setSelectedSession(null);
      form.setFieldValue("forumTableId", undefined);

      return;
    }

    const newSelectedSession = sessions.find((session) => session.sessionId === value);
    const newSelectedSeminar = streamsAndSeminarsAndSessions?.data?.seminars?.find(
      (seminar) => seminar.seminarId === newSelectedSession?.seminarId,
    );
    const newSelectedStream = streams.find((stream) => stream.id === newSelectedSession?.streamId);

    setSeminars(
      streamsAndSeminarsAndSessions?.data?.seminars?.filter(
        (seminar) => seminar.streamId === newSelectedSession?.streamId,
      ) || [],
    );

    setSessions(
      streamsAndSeminarsAndSessions?.data?.sessions?.filter(
        (session) => session.seminarId === newSelectedSession?.seminarId,
      ) || [],
    );

    setSelectedSession(newSelectedSession || null);
    setSelectedSeminar(newSelectedSeminar || null);
    setSelectedStream(newSelectedStream || null);

    form.setFieldValue("forumStreamId", newSelectedSession?.streamId);
    form.setFieldValue("forumSeminarId", newSelectedSession?.seminarId);
    form.setFieldValue("forumTableId", undefined);

    form.validateFields(["forumStreamId", "forumSeminarId", "forumSessionId"]);
  };

  const handleAttendingAsSpeakerChange = (e: RadioChangeEvent) => {
    const value = e.target.value as boolean;

    setAttendingAsSpeaker(value);

    if (value) {
      form.setFieldValue("forumTableId", undefined);

      if (selectedSeminar) {
        setAttendees(
          attendeesData?.data?.items?.filter((attendee) =>
            seminarSpeakers?.some((speaker: SpeakerForumAttendee) => speaker.id === attendee.attendId),
          ) || [],
        );

        const selectedAttendeeId = form.getFieldValue("attendeeId");

        const selectedAttendee = seminarSpeakers.find((speaker) => speaker.id === selectedAttendeeId);

        if (!selectedAttendee) {
          form.setFieldValue("attendeeId", undefined);
        }
      }
    } else {
      setAttendees(attendeesData?.data?.items || []);
    }
  };

  return (
    <Drawer
      className="manual-scheduling-drawer"
      title={t("Add To Conf Session")}
      placement="right"
      onClose={closeDrawer}
      open={visible}
      contentWrapperStyle={{ minWidth: "50%" }}
      destroyOnClose
    >
      <Form
        form={form}
        id="manualSchedulingForm"
        name="manual-scheduling-form"
        labelCol={{ span: 4 }}
        onFinish={handleSubmit}
      >
        <div className="form-layout">
          <div>
            <Form.Item
              label={t("Stream")}
              name="forumStreamId"
              rules={[
                {
                  required: true,
                  message: t("Stream is required!"),
                },
              ]}
            >
              {isStreamsAndSeminarsAndSessionsLoading ? (
                <Spin />
              ) : (
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="title"
                  optionLabelProp="label"
                  onChange={handleStreamChange}
                  filterOption={(input, option) =>
                    (option?.title ? String(option.title) : "").toLowerCase().includes(input.toLowerCase())
                  }
                  dropdownRender={(menu) => (
                    <div>
                      <Row
                        style={{
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: "rgba(0,0,0,.65)",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Col span={7}>
                          <div style={ellipsisCell} title={t("Type")}>
                            {t("Type")}
                          </div>
                        </Col>
                        <Col span={17}>
                          <div style={ellipsisCell} title={t("Description")}>
                            {t("Description")}
                          </div>
                        </Col>
                      </Row>
                      {menu}
                    </div>
                  )}
                >
                  {streams.map((stream) => (
                    <Option
                      key={stream?.id}
                      value={stream?.id}
                      title={`${stream?.type} ${stream?.description}`}
                      label={
                        <Row>
                          <Col span={7}>
                            <div style={ellipsisCell} title={stream?.type}>
                              {stream?.type}
                            </div>
                          </Col>
                          <Col span={17}>
                            <div style={ellipsisCell} title={stream?.description}>
                              {stream?.description}
                            </div>
                          </Col>
                        </Row>
                      }
                    >
                      <Row>
                        <Col span={7}>
                          <div style={ellipsisCell} title={stream?.type}>
                            {stream?.type}
                          </div>
                        </Col>
                        <Col span={17}>
                          <div style={ellipsisCell} title={stream?.description}>
                            {stream?.description}
                          </div>
                        </Col>
                      </Row>
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            <Form.Item
              label={t("Seminar")}
              name="forumSeminarId"
              rules={[
                {
                  required: true,
                  message: t("Seminar is required!"),
                },
              ]}
            >
              {isStreamsAndSeminarsAndSessionsLoading ? (
                <Spin />
              ) : (
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="title"
                  optionLabelProp="label"
                  onChange={handleSeminarChange}
                  filterOption={(input, option) =>
                    (option?.title ? String(option.title) : "").toLowerCase().includes(input.toLowerCase())
                  }
                  dropdownRender={(menu) => (
                    <div>
                      <Row
                        style={{
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: "rgba(0,0,0,.65)",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Col span={4}>
                          <div style={ellipsisCell} title={t("Code")}>
                            {t("Code")}
                          </div>
                        </Col>
                        <Col span={20}>
                          <div style={ellipsisCell} title={t("Title")}>
                            {t("Title")}
                          </div>
                        </Col>
                      </Row>
                      {menu}
                    </div>
                  )}
                >
                  {seminars.map((seminar) => (
                    <Option
                      key={seminar?.seminarId}
                      value={seminar?.seminarId}
                      title={`${seminar?.seminarCode} ${seminar?.seminarName}`}
                      label={
                        <Row>
                          <Col span={7}>
                            <div style={ellipsisCell} title={seminar?.seminarCode}>
                              {seminar?.seminarCode}
                            </div>
                          </Col>
                          <Col span={17}>
                            <div style={ellipsisCell} title={seminar?.seminarName}>
                              {seminar?.seminarName}
                            </div>
                          </Col>
                        </Row>
                      }
                    >
                      <Row>
                        <Col span={4}>
                          <div style={ellipsisCell} title={seminar?.seminarCode}>
                            {seminar?.seminarCode}
                          </div>
                        </Col>
                        <Col span={20}>
                          <div style={ellipsisCell} title={seminar?.seminarName}>
                            {seminar?.seminarName}
                          </div>
                        </Col>
                      </Row>
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            <Form.Item
              label={t("Session")}
              name="forumSessionId"
              rules={[
                {
                  required: true,
                  message: t("Session is required!"),
                },
              ]}
            >
              {isStreamsAndSeminarsAndSessionsLoading ? (
                <Spin />
              ) : (
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="title"
                  optionLabelProp="label"
                  onChange={handleSessionChange}
                  filterOption={(input, option) =>
                    (option?.title ? String(option.title) : "").toLowerCase().includes(input.toLowerCase())
                  }
                  dropdownRender={(menu) => (
                    <div>
                      <Row
                        style={{
                          padding: "8px 12px",
                          fontWeight: 800,
                          color: "rgba(0,0,0,.65)",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <Col span={7}>
                          <div style={ellipsisCell} title={t("Facility")}>
                            {t("Facility")}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={ellipsisCell} title={t("Timeslot")}>
                            {t("Timeslot")}
                          </div>
                        </Col>
                        <Col span={5}>
                          <div style={ellipsisCell} title={t("Speaker")}>
                            {t("Speakers")}
                          </div>
                        </Col>
                      </Row>
                      {menu}
                    </div>
                  )}
                >
                  {sessions?.map((session) => (
                    <Option
                      key={session.sessionId}
                      value={session.sessionId}
                      title={`${session.forumFacilityName} ${session.startTime} - ${session.endTime}`}
                      label={
                        <Row>
                          <Col span={7}>
                            <div style={ellipsisCell} title={session.forumFacilityName}>
                              {session.forumFacilityName}
                            </div>
                          </Col>
                          <Col span={12}>
                            <div style={ellipsisCell} title={`${session.startTime} - ${session.endTime}`}>
                              {session.startTime &&
                                session.endTime &&
                                `${getFormattedRangeDateTime(session.startTime, session.endTime, forum)}`}
                            </div>
                          </Col>
                          <Col span={5}>
                            <div style={ellipsisCell} title={session.speakersCount?.toString() || "0"}>
                              {session.speakersCount?.toString() || "0"}
                            </div>
                          </Col>
                        </Row>
                      }
                    >
                      <Row>
                        <Col span={7}>
                          <div style={ellipsisCell} title={session.forumFacilityName}>
                            {session.forumFacilityName}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={ellipsisCell} title={`${session.startTime} - ${session.endTime}`}>
                            {session.startTime &&
                              session.endTime &&
                              `${getFormattedRangeDateTime(session.startTime, session.endTime, forum)}`}
                          </div>
                        </Col>
                        <Col span={5}>
                          <div style={ellipsisCell} title={session.speakersCount?.toString() || "0"}>
                            {session.speakersCount?.toString() || "0"}
                          </div>
                        </Col>
                      </Row>
                    </Option>
                  ))}
                </Select>
              )}
            </Form.Item>
            <Divider />
            <div style={{ marginTop: 16, marginLeft: 48, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontWeight: "bold" }}>{t("Attendee Details")}</div>
              <div style={{ marginLeft: 12, display: "flex", flexDirection: "column" }}>
                <Form.Item
                  label={t("Attending as Speaker")}
                  name="attendingAsSpeaker"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  style={{ marginBottom: 4 }}
                >
                  <Radio.Group
                    disabled={isSeminarSpeakersLoading || !selectedSeminar}
                    onChange={handleAttendingAsSpeakerChange}
                  >
                    <Radio value={false}>{t("No")}</Radio>
                    <Radio value={true}>{t("Yes")}</Radio>
                  </Radio.Group>
                </Form.Item>
                {!selectedSeminar && (
                  <div style={{ color: "orange", marginTop: 4, marginBottom: 4 }}>
                    {t("Select a seminar to enable this option.")}
                  </div>
                )}
                <Form.Item
                  label={t("Attendee")}
                  name="attendeeId"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  style={{ marginBottom: 0 }}
                  rules={[
                    {
                      required: true,
                      message: t("Attendee is required!"),
                    },
                  ]}
                >
                  {isAttendeeLoading ? (
                    <Spin />
                  ) : (
                    <Select
                      showSearch
                      allowClear
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ? String(option.label) : "").toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownRender={(menu) => (
                        <div>
                          <Row
                            style={{
                              padding: "8px 12px",
                              fontWeight: 800,
                              color: "rgba(0,0,0,.65)",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <Col span={6}>{t("Name")}</Col>
                            <Col span={8}>{t("Job Title")}</Col>
                            <Col span={6}>{t("Company")}</Col>
                            <Col span={4}>{t("Participation Type")}</Col>
                          </Row>
                          {menu}
                        </div>
                      )}
                    >
                      {attendees.map((attendee) => (
                        <Option
                          key={attendee.attendId}
                          value={attendee.attendId}
                          label={`${attendee.fullName} ${attendee.jobTitle} ${attendee.forumCompany} ${attendee.participationCode}`}
                        >
                          <Row>
                            <Col span={6}>
                              <div style={ellipsisCell} title={attendee.fullName}>
                                {attendee.fullName}
                              </div>
                            </Col>
                            <Col span={8}>
                              <div style={ellipsisCell} title={attendee.jobTitle || ""}>
                                {attendee.jobTitle}
                              </div>
                            </Col>
                            <Col span={6}>
                              <div style={ellipsisCell} title={attendee.forumCompany || ""}>
                                {attendee.forumCompany}
                              </div>
                            </Col>
                            <Col span={4}>
                              <div style={ellipsisCell} title={attendee.participationCode || ""}>
                                {attendee.participationCode}
                              </div>
                            </Col>
                          </Row>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>

                {attendingAsSpeaker && (
                  <div style={{ color: "orange", marginTop: 4, marginBottom: 4 }}>
                    {t("Only speakers allocated to selected seminar are available.")}
                  </div>
                )}
                {attendingAsSpeaker &&
                selectedSession &&
                selectedSession.speakersCount &&
                selectedSession.speakersCount > 0 ? (
                  <div style={{ color: "orchid" }}>
                    {t(
                      `Selected session has ${selectedSession.speakersCount} speaker${
                        selectedSession.speakersCount > 1 ? "s" : ""
                      } assigned.`,
                    )}
                  </div>
                ) : null}
              </div>
            </div>
            <Divider />
            <div style={{ marginTop: 16, marginLeft: 48, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontWeight: "bold" }}>{t("Table Allocation")}</div>
              <div style={{ marginLeft: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                <Form.Item
                  label={t("Table")}
                  name="forumTableId"
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                  style={{ marginBottom: 4 }}
                  rules={[
                    {
                      required: isTableRequired,
                      message: t("Table is required!"),
                    },
                  ]}
                >
                  {isTablesLoading && !!selectedSession && !attendingAsSpeaker ? (
                    <Spin />
                  ) : (
                    <Select
                      showSearch
                      allowClear
                      disabled={!selectedSession || attendingAsSpeaker || (isTablesSuccess && tables.length === 0)}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        (option?.label ? String(option.label) : "").toLowerCase().includes(input.toLowerCase())
                      }
                      dropdownRender={(menu) => (
                        <div>
                          <Row
                            style={{
                              padding: "8px 12px",
                              fontWeight: 800,
                              color: "rgba(0,0,0,.65)",
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            <Col span={6}>{t("Table Number")}</Col>
                            <Col span={8}>{t("Table Type")}</Col>
                            <Col span={6}>{t("Seats Total")}</Col>
                            <Col span={4}>{t("Available")}</Col>
                          </Row>
                          {menu}
                        </div>
                      )}
                    >
                      {tables.map((table) => (
                        <Option
                          key={table.tableId}
                          value={table.tableId}
                          label={`${table.tableNumber} ${table.tableType} ${table.totalSeats} ${table.availableSeats}`}
                        >
                          <Row>
                            <Col span={6}>{table.tableNumber}</Col>
                            <Col span={8}>{table.tableType && TableType.toDisplayName(table.tableType)}</Col>
                            <Col span={6}>{table.totalSeats}</Col>
                            <Col span={4}>{table.availableSeats}</Col>
                          </Row>
                        </Option>
                      ))}
                    </Select>
                  )}
                </Form.Item>
                {!selectedSession && !attendingAsSpeaker && (
                  <div style={{ color: "orange", marginBottom: 4 }}>{t("Select a session to enable this option.")}</div>
                )}
                {selectedSession && !attendingAsSpeaker && isTablesSuccess && tables.length === 0 && (
                  <div style={{ color: "orange", marginBottom: 4 }}>
                    {t("No tables are configured for this facility. Table allocation is optional.")}
                  </div>
                )}
                {selectedSession && !attendingAsSpeaker && isTablesError && (
                  <div style={{ color: "red", marginBottom: 4 }}>
                    {t("Failed to load tables for the selected session. Please try again.")}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <Divider />
            <Row>
              <Col span={3}>
                <Button onClick={closeDrawer}>{t("Cancel")}</Button>
              </Col>
              <Col span={18} />
              <Col span={3}>
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={addToConferenceSessionMutation.isLoading}
                    disabled={addToConferenceSessionMutation.isLoading || isTableLookupBlockingSubmit}
                  >
                    {t("Create")}
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </div>
        </div>
      </Form>
    </Drawer>
  );
};
